import { useCallback, useContext, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../../../components/common/Button";
import debounce from "lodash.debounce";
import InputAndSelect from "../../../../components/common/InputAndSelect";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import { formatCurrencyCodeAmount } from "../../../../utils/currency";
import { useUserData } from "../../../../app/hooks/useForm";
import { swapCurrency } from "../../redux/slice";
import { encryptAlgorithm } from "../../../../utils/encryptionAlgorithm";
import { merchantBusinessKeys } from "../../../Settings/settingsSlice";
import Loader from "../../../../components/common/Loader";
import { savePaymentData } from "../../redux/formDataSlice";
import {
  Heading,
  InputLabel,
  InfoBox,
  FormWrapper,
  InputAreaWrapper,
  RateWrapper,
} from "./styles";
import { NetworkErrorAlertContext } from "../../../../context/NetworkErrorAlert";
import CurrencyExchange from "../CurrencyExchange/CurrencyExchange";
import { CURRENCIES, CURRENCIES_TO_ALLOW_FOR_SPECIFIC_STABLE_COINS } from "../../utils";

type AmountProps = {
  setStep: (data: string) => void;
};

const Amount: React.FC<AmountProps> = ({ setStep }) => {
  const [userData, updateField, setUserData] = useUserData();
  const { wallets, loading } = useAppSelector((state) => state?.wallets || {});
  const { onShowAlert: onShowErrorAlert } =
    useContext(NetworkErrorAlertContext) || {};

  const { keys } = useAppSelector((state) => state.settings);
  const debounceRef = useRef<any>(null);

  const { businessId } = useAppSelector((state) => state.users);

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const MIN_AMOUNT = 0;
  const userDataRef = useRef(userData);
  const keysRef = useRef(keys);

  const RATE_VALIDATOR =
    (userData.destinationAmount > MIN_AMOUNT &&
      (userData.convertedAmount as number) > MIN_AMOUNT) ||
    userData.destinationCurrency &&
    userData?.sourceCurrency && (userData.amount || userData.convertedAmount);
  
  useEffect(() => {
    userDataRef.current = userData;
  }, [userData]);

  useEffect(() => {
    keysRef.current = keys;
  }, [keys]);

  useEffect(() => {
    if (businessId) {
      dispatch(merchantBusinessKeys(businessId));
    }
  }, [businessId, dispatch]);

  if (!wallets.length) {
    return <p>Loading wallets...</p>;
  }

  const handleNextStep = () => {
    navigate("/wallets/swap/currency/?type=amount");
    setStep("review");
    dispatch(savePaymentData(userData));
  };

  const allowedCurrencies = Object.keys(CURRENCIES);

  const createFilteredWallets = (excludeCurrency: string) =>
    wallets
      .filter(
        (wallet) =>
          wallet.currency !== excludeCurrency &&
          allowedCurrencies.includes(wallet.currency)
      )
      .filter((wallet) => {
        const currenciesAllowedForStableCoin = CURRENCIES_TO_ALLOW_FOR_SPECIFIC_STABLE_COINS[excludeCurrency?.toUpperCase()];
        if (currenciesAllowedForStableCoin) {
          return currenciesAllowedForStableCoin.includes(wallet.currency?.toUpperCase());
        }

        const isPossibleStableCoinPairing = excludeCurrency && CURRENCIES_TO_ALLOW_FOR_SPECIFIC_STABLE_COINS[wallet.currency?.toUpperCase()];
        if (isPossibleStableCoinPairing) {
          return isPossibleStableCoinPairing.includes(excludeCurrency?.toUpperCase());
        }

        return true;
      })
      .map((wallet) => ({
        label: wallet.currency,
        value: wallet.currency,
      }));

  const filteredWallets = createFilteredWallets(userData.destinationCurrency);
  const filteredDestinationWallets = createFilteredWallets(userData.sourceCurrency);

  const findWalletByCurrency = (currency: string) =>
    wallets.find(
      (wallet) =>
        wallet.currency === currency &&
        allowedCurrencies.includes(wallet.currency)
    );

  const findWalletByCurrencyBalance = findWalletByCurrency(userData.destinationCurrency);
  const findWalletBySourceBalance = findWalletByCurrency(userData.sourceCurrency);



  const debounceRequestQuote = useCallback(
    debounce(async (requestData) => {
      const currentKeys = keysRef.current;
      const currentUserData = userDataRef.current;
      if (
        !currentUserData.sourceCurrency ||
        !currentUserData.destinationCurrency
      ) {
        return;
      }

      if (!currentKeys?.data?.encryptionKey || !currentKeys?.data?.publicKey) {
        return;
      }

      const encryptedData = encryptAlgorithm(
        requestData,
        currentKeys.data.encryptionKey,
      );
      const payload = { message: encryptedData };
      const action = await dispatch(
        swapCurrency({ message: payload, token: currentKeys.data.publicKey }),
      );

      if (swapCurrency.fulfilled.match(action)) {
        const { data } = action.payload;

        setUserData((prev) => ({
          ...prev,
          rate: data.rate,
          destinationAmount:
            requestData.mode === "SOURCE"
              ? data.destinationAmount
              : prev.destinationAmount,
          convertedAmount:
            requestData.mode === "SOURCE"
              ? prev.convertedAmount
              : data.sourceAmount,
          disable: false,
          quoteToken: data.quoteToken,
          publicKey: currentKeys.data.publicKey,
          encryptionKey: currentKeys.data.encryptionKey,
        }));
      } else if (swapCurrency.rejected.match(action)) {
        onShowErrorAlert("", String(action.payload));
      }
    }, 1000),
    [dispatch],
  );

  useEffect(() => {
    debounceRef.current = debounceRequestQuote;
  }, [debounceRequestQuote]);

  const makeQuoteRequest = useCallback((requestData) => {
    debounceRef.current?.cancel?.();
    debounceRef.current?.(requestData);
  }, []);

  const clearQuoteData = useCallback(() => {
    debounceRef.current?.cancel?.();
    setUserData((prev) => ({
      ...prev,
      rate: null,
      disable: true,
    }));
  }, [userData]);

  const handleSwapCurrencies = useCallback(() => {
    debounceRef.current?.cancel?.();

    setUserData((prev) => {
      const updatedData = {
        ...prev,
        sourceCurrency: prev.destinationCurrency,
        destinationCurrency: prev.sourceCurrency,
        destinationAmount: Number(prev.convertedAmount),
        convertedAmount: prev.destinationAmount,
        rate: null,
        disable: false,
      };

      makeQuoteRequest({
        sourceCurrency: updatedData.sourceCurrency,
        destinationCurrency: updatedData.destinationCurrency,
        ...(updatedData.convertedAmount
          ? { sourceAmount: updatedData.convertedAmount, mode: "SOURCE" }
          : {}),
      });

      return updatedData;

    });
  }, []);


  const insufficientBalance =
    Number(userData.convertedAmount) > Number(findWalletBySourceBalance?.availableBalance)
  const disableButton = loading || !RATE_VALIDATOR || insufficientBalance || userData.disable


  return (
    <InputAreaWrapper>
      <Loader isLoading={loading} />
      <Heading>How much do you want to swap?</Heading>

      <FormWrapper>
        <div className="w-full">
          <InputLabel className="font-medium text-sm">Buying</InputLabel>
          <InputAndSelect
            options={filteredDestinationWallets}
            amountValue={userData.destinationAmount}
            onAmountChange={(value) => {
              updateField("destinationAmount")(value);

              if (!value || (value as number) <= MIN_AMOUNT) {
                clearQuoteData();
                updateField("convertedAmount")(null);
                return;
              }

              const currentUserData = userDataRef.current;
              makeQuoteRequest({
                sourceCurrency: currentUserData.sourceCurrency,
                destinationCurrency: currentUserData.destinationCurrency,
                destinationAmount: value,
                mode: "DESTINATION"
              });
            }}
            onSelectChange={(currency) => {
              updateField("destinationCurrency")(currency);

              const currentUserData = userDataRef.current;
              if (currentUserData.destinationAmount > MIN_AMOUNT) {
                makeQuoteRequest({
                  sourceCurrency: currentUserData.sourceCurrency,
                  destinationCurrency: currency,
                  destinationAmount: Number(currentUserData.destinationAmount),
                  mode: "DESTINATION"
                });
              } else if ((currentUserData.convertedAmount as number) > MIN_AMOUNT) {
                makeQuoteRequest({
                  sourceCurrency: currentUserData.sourceCurrency,
                  destinationCurrency: currency,
                  sourceAmount: Number(currentUserData.convertedAmount),
                  mode: "SOURCE",
                });
              }
            }}
            selectValue={userData.destinationCurrency}
            placeholder="Conversion amount"
            width="100%"
          />
          {userData.destinationCurrency && (
            <InfoBox className="text-xs font-normal text-[#8F8F8F]">
              You have{" "}
              <span className="item-content">
                {formatCurrencyCodeAmount(
                  userData.destinationCurrency,
                  findWalletByCurrencyBalance?.availableBalance || MIN_AMOUNT,
                )}
              </span>{" "}
              available in your balance.
            </InfoBox>
          )}
        </div>

        {RATE_VALIDATOR && (
          <RateWrapper>
            <CurrencyExchange
              toCurrency={userData.destinationCurrency}
              fromCurrency={userData.sourceCurrency}
              fromAmount={1}
              toAmount={userData.rate as number}
              onClick={handleSwapCurrencies}
            />
          </RateWrapper>
        )}

        <div className="w-full">
          <InputLabel>Paying</InputLabel>
          <InputAndSelect
            options={filteredWallets}
            placeholder="Converted amount"
            onSelectChange={(currency) => {
              updateField("sourceCurrency")(currency);

              const currentUserData = userDataRef.current;
              if (currentUserData.destinationAmount > MIN_AMOUNT) {
                makeQuoteRequest({
                  sourceCurrency: currency,
                  destinationCurrency: currentUserData.destinationCurrency,
                  destinationAmount: currentUserData.destinationAmount,
                  mode: "DESTINATION"
                });
              } else if ((currentUserData.convertedAmount as number) > MIN_AMOUNT) {
                makeQuoteRequest({
                  sourceCurrency: currency,
                  destinationCurrency: currentUserData.destinationCurrency,
                  sourceAmount: currentUserData.convertedAmount,
                  mode: "SOURCE",
                });
              }
            }}
            onAmountChange={(value) => {
              updateField("convertedAmount")(value);

              if (!value || (value as number) <= MIN_AMOUNT) {
                clearQuoteData();
                updateField("destinationAmount")(null);
                return;
              }

              const currentUserData = userDataRef.current;
              makeQuoteRequest({
                sourceCurrency: currentUserData.sourceCurrency,
                destinationCurrency: currentUserData.destinationCurrency,
                sourceAmount: value,
                mode: "SOURCE",
              });
            }}
            selectValue={userData.sourceCurrency}
            amountValue={userData.convertedAmount}
            width="100%"
          />
          {userData.sourceCurrency && (
            <InfoBox className="text-xs font-normal text-[#8F8F8F]">
              You have{" "}
              <span className="item-content">
                {formatCurrencyCodeAmount(
                  userData.sourceCurrency,
                  findWalletBySourceBalance?.availableBalance || MIN_AMOUNT,
                )}
              </span>{" "}
              available in your balance.
            </InfoBox>
          )}
        </div>

        <Button
          width="100%"
          height="48px"
          label="Continue"
          fontSize="12px"
          onClick={handleNextStep}
          disabled={disableButton}
        />
      </FormWrapper>
    </InputAreaWrapper>
  );
};

export default Amount;